import { useState, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { usePermissions } from '@/hooks/usePermissions'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Mail, Loader2, CheckCircle2, XCircle, Send, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import type { VistaSaldoCliente } from '@/types/database'
import { emailService } from '@/services/emailService'
import { movimientosService } from '@/services/movimientosService'
import { emailLogKeys } from '@/hooks/useEmailLog'
import { useAuth0 } from '@auth0/auth0-react'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import { useSupabase } from '@/lib/supabase-context'
import { formatCurrency } from '@/lib/utils'

const BATCH_SIZE = 5

interface EnvioMasivoEstadoCuentaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clientes: VistaSaldoCliente[]
}

interface ResultadoEnvio {
  clienteId: string
  nombre: string
  success: boolean
  error?: string
}

type EstadoEnvio = 'seleccion' | 'confirmando' | 'enviando' | 'completado'

export function EnvioMasivoEstadoCuentaDialog({
  open,
  onOpenChange,
  clientes,
}: EnvioMasivoEstadoCuentaDialogProps) {
  const { puede } = usePermissions()
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set())
  const [estadoEnvio, setEstadoEnvio] = useState<EstadoEnvio>('seleccion')
  const [progreso, setProgreso] = useState(0)
  const [totalEnvios, setTotalEnvios] = useState(0)
  const [resultados, setResultados] = useState<ResultadoEnvio[]>([])

  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const { data: configEstudio } = useConfiguracionEstudio()
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()

  // Filter: only debtors (saldo > 0 means debe > haber) with email
  const clientesElegibles = useMemo(() => {
    return clientes.filter(
      (c) => c.id && (c.saldo ?? 0) > 0 && c.email
    )
  }, [clientes])

  // Initialize selection when dialog opens
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        // Pre-select all eligible clients
        setSeleccionados(
          new Set(clientesElegibles.map((c) => c.id!))
        )
        setEstadoEnvio('seleccion')
        setProgreso(0)
        setTotalEnvios(0)
        setResultados([])
      }
      onOpenChange(newOpen)
    },
    [clientesElegibles, onOpenChange]
  )

  const toggleCliente = (clienteId: string) => {
    setSeleccionados((prev) => {
      const next = new Set(prev)
      if (next.has(clienteId)) {
        next.delete(clienteId)
      } else {
        next.add(clienteId)
      }
      return next
    })
  }

  const toggleTodos = () => {
    if (seleccionados.size === clientesElegibles.length) {
      setSeleccionados(new Set())
    } else {
      setSeleccionados(new Set(clientesElegibles.map((c) => c.id!)))
    }
  }

  const handleSolicitarEnvio = () => {
    // ORR-200: flag propio enviarEstadoCuenta (reemplaza el proxy editarCliente de B1)
    if (!puede.enviarEstadoCuenta) return
    if (seleccionados.size === 0) return
    setEstadoEnvio('confirmando')
  }

  const handleCancelarConfirmacion = () => {
    setEstadoEnvio('seleccion')
  }

  const handleConfirmarEnvio = async () => {
    if (!supabase || seleccionados.size === 0 || estadoEnvio !== 'confirmando' || !puede.enviarEstadoCuenta) return
    if (!organizacionId) {
      toast.error('No se pudo identificar la organización. Recargá la página.')
      return
    }

    const clientesAEnviar = clientesElegibles.filter((c) =>
      seleccionados.has(c.id!)
    )

    setEstadoEnvio('enviando')
    setTotalEnvios(clientesAEnviar.length)
    setProgreso(0)
    setResultados([])

    let token: string
    try {
      token = await getAccessTokenSilently()
    } catch (err) {
      console.error('Error obteniendo token:', err)
      toast.error('Error de autenticación. Intenta recargar la página.')
      setEstadoEnvio('seleccion')
      return
    }

    const resultadosEnvio: ResultadoEnvio[] = []

    const enviarParaCliente = async (cliente: VistaSaldoCliente): Promise<ResultadoEnvio> => {
      try {
        const movimientos = await movimientosService.obtenerPorCliente(
          supabase,
          organizacionId,
          cliente.id!
        )

        const movimientosParaEmail = movimientos
          .sort(
            (a, b) =>
              new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
          )
          .map((mov) => ({
            fecha: mov.fecha,
            concepto: mov.concepto,
            debe: mov.debe ?? 0,
            haber: mov.haber ?? 0,
          }))

        const saldo = cliente.saldo ?? 0

        const result = await emailService.enviarEstadoCuenta(
          token,
          { id: cliente.id!, nombre: cliente.nombre || 'Cliente', email: cliente.email! },
          movimientosParaEmail,
          saldo,
          organizacionId,
          configEstudio?.telefono || undefined
        )

        return {
          clienteId: cliente.id!,
          nombre: cliente.nombre || 'Cliente',
          success: result.success,
          error: result.error,
        }
      } catch (err) {
        return {
          clienteId: cliente.id!,
          nombre: cliente.nombre || 'Cliente',
          success: false,
          error: err instanceof Error ? err.message : 'Error desconocido',
        }
      }
    }

    for (let i = 0; i < clientesAEnviar.length; i += BATCH_SIZE) {
      const batch = clientesAEnviar.slice(i, i + BATCH_SIZE)
      const batchResults = await Promise.allSettled(
        batch.map(enviarParaCliente)
      )

      for (const result of batchResults) {
        resultadosEnvio.push(
          result.status === 'fulfilled'
            ? result.value
            : { clienteId: '', nombre: '', success: false, error: 'Error inesperado' }
        )
      }

      setProgreso(Math.min(i + BATCH_SIZE, clientesAEnviar.length))
      setResultados([...resultadosEnvio])
    }

    setEstadoEnvio('completado')
    queryClient.invalidateQueries({ queryKey: emailLogKeys.all })

    const enviados = resultadosEnvio.filter((r) => r.success).length
    const errores = resultadosEnvio.filter((r) => !r.success).length

    if (errores === 0) {
      toast.success(`${enviados} estados de cuenta enviados correctamente`)
    } else {
      toast.warning(`Enviados: ${enviados}, Errores: ${errores}`)
    }
  }

  const enviadosCount = resultados.filter((r) => r.success).length
  const erroresCount = resultados.filter((r) => !r.success).length
  const porcentajeProgreso =
    totalEnvios > 0 ? (progreso / totalEnvios) * 100 : 0

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Enviar Estados de Cuenta
          </DialogTitle>
          <DialogDescription>
            {(estadoEnvio === 'seleccion' || estadoEnvio === 'confirmando') &&
              'Selecciona los clientes deudores a los que deseas enviar su estado de cuenta por email.'}
            {estadoEnvio === 'enviando' &&
              `Enviando estado de cuenta ${progreso} de ${totalEnvios}...`}
            {estadoEnvio === 'completado' && 'Proceso de envío finalizado.'}
          </DialogDescription>
        </DialogHeader>

        {/* Selection phase */}
        {(estadoEnvio === 'seleccion' || estadoEnvio === 'confirmando') && (
          <div className="flex-1 overflow-hidden flex flex-col gap-3">
            {clientesElegibles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No hay clientes deudores con email configurado
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleTodos}
                  >
                    {seleccionados.size === clientesElegibles.length
                      ? 'Deseleccionar todos'
                      : 'Seleccionar todos'}
                  </Button>
                  <Badge variant="secondary">
                    {seleccionados.size} de {clientesElegibles.length}{' '}
                    seleccionados
                  </Badge>
                </div>

                <div className="flex-1 overflow-y-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={
                              seleccionados.size ===
                              clientesElegibles.length
                            }
                            onCheckedChange={toggleTodos}
                          />
                        </TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="text-right">
                          Saldo
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clientesElegibles.map((cliente) => (
                        <TableRow
                          key={cliente.id}
                          className="cursor-pointer"
                          onClick={() => toggleCliente(cliente.id!)}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={seleccionados.has(cliente.id!)}
                              onCheckedChange={() =>
                                toggleCliente(cliente.id!)
                              }
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {cliente.nombre}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {cliente.email}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="font-semibold text-red-600 tabular-nums">
                              {formatCurrency(cliente.saldo ?? 0)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Sending phase */}
        {estadoEnvio === 'enviando' && (
          <div className="space-y-4 py-4">
            <Progress value={porcentajeProgreso} className="h-3" />
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Enviando {progreso} de {totalEnvios}...
            </div>
            {resultados.length > 0 && (
              <div className="flex items-center justify-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  {enviadosCount} enviados
                </span>
                {erroresCount > 0 && (
                  <span className="flex items-center gap-1 text-red-600">
                    <XCircle className="h-4 w-4" />
                    {erroresCount} errores
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Completed phase */}
        {estadoEnvio === 'completado' && (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-center gap-4 text-sm">
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                {enviadosCount} enviados
              </span>
              {erroresCount > 0 && (
                <span className="flex items-center gap-1 text-red-600">
                  <XCircle className="h-4 w-4" />
                  {erroresCount} errores
                </span>
              )}
            </div>

            {erroresCount > 0 && (
              <div className="rounded-md border overflow-y-auto max-h-48">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resultados
                      .filter((r) => !r.success)
                      .map((r) => (
                        <TableRow key={r.clienteId}>
                          <TableCell className="font-medium">
                            {r.nombre}
                          </TableCell>
                          <TableCell className="text-sm text-red-600">
                            {r.error}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {(estadoEnvio === 'seleccion' || estadoEnvio === 'confirmando') && (
            <>
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSolicitarEnvio}
                disabled={seleccionados.size === 0 || !supabase || estadoEnvio === 'confirmando' || !puede.enviarEstadoCuenta}
              >
                <Send className="h-4 w-4 mr-2" />
                Enviar ({seleccionados.size})
              </Button>
            </>
          )}
          {estadoEnvio === 'completado' && (
            <Button onClick={() => handleOpenChange(false)}>
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Confirmation dialog */}
      <AlertDialog
        open={estadoEnvio === 'confirmando'}
        onOpenChange={(open) => { if (!open) handleCancelarConfirmacion() }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirmar envío masivo
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se enviarán <strong>{seleccionados.size}</strong> emails de estado de cuenta.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelarConfirmacion}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmarEnvio}>
              Confirmar envío
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}
